import {useHistory, useLocation} from 'react-router-dom';
import * as React from 'react';
import {twMerge} from 'tailwind-merge';
import services from 'services';
import {Tooltip} from 'components/ui/Tooltip';
import {LISTABLE_TYPES, LISTABLE_TYPES_MODELS} from 'dicts/lists';
import {getListItemBuilding} from 'utils/lists';
import {createReferrerUrl, encodeQuery} from 'utils/url';
import {User, Phone, Home, Check, Loader2, Trash2, Briefcase} from 'lucide-react';

let intl = null;
services.waitFor('intl').then(x => (intl = x));

const ActionButton = ({
	children,
	onClick = () => {},
	className = '',
	disabled = false,
	tooltip = '',
}) => {
	const button = (
		<button
			onClick={onClick}
			type="button"
			className={twMerge(
				'flex items-center justify-center gap-x-2 bg-gray-200 hover:bg-gray-300 w-7 h-7 rounded-full text-sm transition-colors duration-100',
				className,
				disabled && 'opacity-30 pointer-events-none',
			)}
			disabled={disabled}
		>
			{children}
		</button>
	);

	if (!tooltip) {
		return button;
	}

	return (
		<Tooltip placement="top" size="sm" trigger={button}>
			{tooltip}
		</Tooltip>
	);
};

export default function ListItemActionButtons({
	listItem,
	referrerUrl,
	onProcessItem,
	isProcessing,
	onRemoveFromList,
	className = '',
}) {
	const itemType = LISTABLE_TYPES_MODELS[listItem.listableType];
	const listable = listItem?.listable?.data;

	const history = useHistory();
	const location = useLocation();
	const resolvedReferrerUrl = referrerUrl || createReferrerUrl(location);
	const building = getListItemBuilding(listItem);
	const buildingId = building?.id ?? null;

	const handleD2dActionClick = React.useCallback(() => {
		if (!buildingId) return;
		history.push(
			`/d2d/buildings/${buildingId}${encodeQuery({
				referrer: 'lists',
				referrerUrl: resolvedReferrerUrl,
				listItemId: listItem.id,
			})}`,
		);
	}, [history, listItem.id, buildingId, resolvedReferrerUrl]);

	const handleCallsActionClick = React.useCallback(() => {
		if (!buildingId) return;
		history.push(
			`/calls/listview/buildings/${buildingId}${encodeQuery({
				referrer: 'lists',
				referrerUrl: resolvedReferrerUrl,
				listItemId: listItem.id,
			})}`,
		);
	}, [history, listItem.id, buildingId, resolvedReferrerUrl]);

	const handleLeadActionClick = React.useCallback(() => {
		if (!listable?.id) return;
		history.push(
			`/marketing/lead/${listable.id}${encodeQuery({
				referrer: 'lists',
				referrerUrl: resolvedReferrerUrl,
				listItemId: listItem.id,
			})}`,
		);
	}, [history, listable?.id, listItem.id, resolvedReferrerUrl]);

	const handleProjectActionClick = React.useCallback(() => {
		if (!listable?.id) return;
		history.push(
			`/project-sales/project/${listable.id}${encodeQuery({
				referrer: 'lists',
				referrerUrl: resolvedReferrerUrl,
				listItemId: listItem.id,
			})}`,
		);
	}, [history, listable?.id, listItem.id, resolvedReferrerUrl]);

	return (
		<div
			className={twMerge(
				'flex items-center gap-x-1.5 lg:ml-auto h-full flex-shrink-0',
				className,
			)}
			onClick={e => e.stopPropagation()}
		>
			{onRemoveFromList && (
				<ActionButton
					onClick={onRemoveFromList}
					className="hover:text-red-600"
					tooltip={intl.formatMessage({id: 'Remove from list'})}
				>
					<Trash2 size={16} />
				</ActionButton>
			)}
			{itemType === LISTABLE_TYPES.projects ? (
				<ActionButton
					onClick={handleProjectActionClick}
					disabled={!listable?.id}
					tooltip={intl.formatMessage({id: 'Project information [tip]'})}
				>
					<Briefcase size={16} />
				</ActionButton>
			) : (
				<ActionButton
					onClick={handleLeadActionClick}
					disabled={itemType !== LISTABLE_TYPES.leads || !listable?.id}
					tooltip={intl.formatMessage({id: 'Lead information [tip]'})}
				>
					<User size={16} />
				</ActionButton>
			)}
			<ActionButton
				onClick={handleCallsActionClick}
				disabled={!buildingId}
				tooltip={intl.formatMessage({id: 'Calls [app]'})}
			>
				<Phone size={16} />
			</ActionButton>
			<ActionButton
				onClick={handleD2dActionClick}
				disabled={!buildingId}
				tooltip={intl.formatMessage({id: 'Visits [app]'})}
			>
				<Home size={16} />
			</ActionButton>
			<ActionButton
				onClick={onProcessItem}
				className={twMerge(
					'bg-gray-200',
					isProcessing && 'opacity-50',
					listItem.processedAt && '!bg-green-500 hover:!bg-green-600 text-white',
				)}
				disabled={isProcessing}
				tooltip={intl.formatMessage({
					id: listItem.processedAt ? 'Mark as unhandled' : 'Mark as handled',
				})}
			>
				{isProcessing ? (
					<Loader2 size={16} className="animate-spin" />
				) : (
					<Check size={16} />
				)}
			</ActionButton>
		</div>
	);
}
